import { useState, useEffect } from 'react';
import { useMemoizedFn } from 'ahooks';
import { IMGE_STAMDARDS_CANVAS } from '@/pages/intelligence/utils/echartsConst';
import { gameIDRex, moveZero } from '@/pages/intelligence/components/Chart/_config/topchartsChartUtils';
import { canvasOption } from '@/pages/intelligence/utils/radiusImg';
import ChartBubble, { TOP_CHARTS_TYPES } from '@/pages/intelligence/components/ChartBubble';
import { getBarChartStaticOption, getBarChartSourceData } from '../utils';
import GameDefault from '@/pages/intelligence/assets/imgs/common/game-default.svg';

import type { IGameType } from '@/pages/intelligence/types/common';
import type { IStoreRankMoreCoreRankPrimaryKey } from '@/pages/intelligence/types/storeRank';

type IImageType = IGameType & {};

/**
 * 可视化X轴label富文本的宽度
 * @param cardWidth 可视化卡片宽度
 * @param length x轴data的长度
 */
const getXAxisLabelWidth = (length: number, cardWidth?: number): number | undefined =>
  typeof cardWidth === 'number' && !Number.isNaN(cardWidth) ? (cardWidth - 100) / length : undefined;

const getCanvasImages = async (
  data: any[],
  cfg: { primaryKey: IStoreRankMoreCoreRankPrimaryKey; imageKey: string; imageType: IImageType },
) => {
  if (!Array.isArray(data)) return {};
  const { primaryKey, imageKey, imageType } = cfg ?? {};
  let images: {
    [key: string]: string;
  } = {};
  let canvasImages: any = {};
  try {
    data.forEach((item: any) => {
      const primaryKeyTmp = gameIDRex(item?.[primaryKey]);
      images[primaryKeyTmp] = item?.[imageKey] || GameDefault;
    });
    canvasImages = await canvasOption(
      images,
      IMGE_STAMDARDS_CANVAS[imageType]?.width,
      IMGE_STAMDARDS_CANVAS[imageType]?.height,
      5,
    );
  } catch (err) {
    console.error(err);
    images = {};
    canvasImages = {};
  }
  return canvasImages;
};

const getAXisLabelCanvasRichs = async (
  data: any[],
  cfg: { primaryKey: IStoreRankMoreCoreRankPrimaryKey; imageKey: string; imageType: IImageType },
) => {
  if (!Array.isArray(data)) return {};
  const { primaryKey, imageType } = cfg || {};

  const canvasImages = await getCanvasImages(data, cfg);

  return data.reduce((cur, next) => {
    const primaryKeyValue = next?.[primaryKey];
    if (!primaryKeyValue) return cur;
    const canvasImage = canvasImages?.[primaryKeyValue];
    const canvasRich = {
      align: 'center',
      width: IMGE_STAMDARDS_CANVAS[imageType]?.width,
      height: IMGE_STAMDARDS_CANVAS[imageType]?.height,
      backgroundColor: !canvasImage
        ? '#fff'
        : {
          image: canvasImage,
        },
    };
    return { ...cur, [primaryKeyValue]: canvasRich };
  }, {});
};

export default function useStoreRankMoreCoreBarChart(props: {
  imageType: IImageType;
  cardWidth?: number;
  primaryKey: IStoreRankMoreCoreRankPrimaryKey;
  imageKey: string;
  tooltipCfg?: {
    headerLeftTitle?: string;
    headerRightTitle?: string;
  };
  dimensions?: string[];
  data: any[];
}) {
  const { imageType, cardWidth, primaryKey, imageKey, tooltipCfg, dimensions: dimensionsOuter, data } = props;
  const dimensions = Array.isArray(dimensionsOuter) ? dimensionsOuter : [];
  const { headerLeftTitle, headerRightTitle } = tooltipCfg || {};

  const [option, setOption] = useState<any>(getBarChartStaticOption({ primaryKey, dimensions }));

  const tooltipFormatter = useMemoizedFn((params: any[]) => {
    const paramsTemp = moveZero(params);
    return ChartBubble({
      topChartsType: TOP_CHARTS_TYPES.STORE_RANK_MORE_BAR,
      chartType: 'bar',
      imageType,
      secondTitleLeft: headerLeftTitle || '',
      secondTitleRight: headerRightTitle || '',
      data: paramsTemp,
    });
  });

  useEffect(() => {
    (async () => {
      if (!Array.isArray(data)) {
        const optionTmp = {
          ...option,
          series: [],
          dataset: {
            ...option.dataset,
            source: [],
          },
        };
        setOption(optionTmp);
        return;
      }

      const xAxisLabelWidth = getXAxisLabelWidth(data?.length, cardWidth);
      const xAxisLabelCanvasRichsTmp = await getAXisLabelCanvasRichs(data, { primaryKey, imageKey, imageType });
      const xAxisData = data.map((item) => item?.[primaryKey]);
      const series = dimensions.map((dimension) => ({
        type: 'bar',
        barWidth: 92,
        name: dimension,
        stack: 'total',
        emphasis: {
          focus: 'none',
        },
      }));
      const sources: any[] = getBarChartSourceData(data, { primaryKey, dimensions });
      const optionTmp = {
        ...option,
        tooltip: {
          ...option?.tooltip,
          formatter: tooltipFormatter,
          backgroundColor: 'rgba(0,0,0,0)',
        },
        xAxis: {
          ...option?.xAxis,
          axisLabel: {
            ...option?.xAxis?.axisLabel,
            width: xAxisLabelWidth,
            rich: xAxisLabelCanvasRichsTmp,
            formatter: (value: string) => {
              const dataItem = data?.find((item) => gameIDRex(item?.[primaryKey]) === value);
              const name = dataItem?.name || '';
              return [`{${value}|}\n\n{avanametar|${name}}`];
            },
          },
          data: xAxisData,
        },
        series,
        dataset: {
          ...option.dataset,
          source: sources,
        },
      };
      setOption(optionTmp);
    })();
  }, [cardWidth, data, tooltipFormatter]);

  return { option };
}
